import React from 'react';

import useLocalStorage from './useLocalStorage';

export interface ColumnVisibilityConfig<Id extends string> {
    id: Id;
    /** Forced visible and not user-toggleable. */
    alwaysVisible?: boolean;
    /** Initial visibility when the user has no stored preference. Defaults to true. */
    defaultVisible?: boolean;
}

export interface ColumnVisibility<Id extends string> {
    isVisible: (id: Id) => boolean;
    toggle: (id: Id) => void;
}

/**
 * localStorage-backed column visibility, reusable across tables. Stores a
 * `Record<id, boolean>` merged over each column's default, so columns added to
 * the config later adopt their default rather than vanishing for existing users.
 * `alwaysVisible` columns are forced on regardless of stored value.
 */
const useColumnVisibility = <Id extends string>(
    storageKey: string,
    columns: ColumnVisibilityConfig<Id>[]
): ColumnVisibility<Id> => {
    const [stored, setStored] = useLocalStorage<Partial<Record<Id, boolean>>>(storageKey, {});

    const resolved = React.useMemo(() => {
        const map = {} as Record<Id, boolean>;
        columns.forEach((column) => {
            if (column.alwaysVisible) {
                map[column.id] = true;
                return;
            }
            const storedValue = stored[column.id];
            map[column.id] =
                storedValue === undefined ? column.defaultVisible !== false : storedValue;
        });
        return map;
    }, [columns, stored]);

    const isVisible = React.useCallback((id: Id) => !!resolved[id], [resolved]);

    const toggleableIds = React.useMemo(
        () => new Set(columns.filter((column) => !column.alwaysVisible).map((column) => column.id)),
        [columns]
    );

    const toggle = React.useCallback(
        (id: Id) => {
            if (!toggleableIds.has(id)) return;
            setStored((prev) => ({ ...prev, [id]: !resolved[id] }));
        },
        [toggleableIds, setStored, resolved]
    );

    return { isVisible, toggle };
};

export default useColumnVisibility;
